import {
  entityKey,
  sideKey,
  statusKey,
  yesNoKey,
  productTypeKey,
} from '../../../lib/shared-value-keys';
import Highcharts from 'highcharts';

export interface LinkedAccount {
  DirectAccountID: string;
  AccountID: string;
  ProcessedDate: string;
  Status: string;
  ReviewerID: string;
  DateUpdated: string;
}

export const caseHistoryKey = [
  { label: 'Case ID', varName: 'CRID' },
  { label: 'Open Date', varName: 'StatusDate', valueMask: 'date' },
  { label: 'Status', varName: 'StatusName' },
];
export const tradeInfoKey = [
  { label: 'Client Name', varName: 'ClientFullName' },
  { label: 'Rep Code', varName: 'RepCode' },
  { label: 'Rep Name', varName: 'RepName' },
  {
    label: 'Account #',
    varName: 'AccountNumDisplay',
    decryptTarget: 'EncryptedAccountID',
  },
  { label: 'Unit', varName: 'UnitID' },
  { label: 'Trade Date', varName: 'TradeDate', valueMask: 'date' },
  { label: 'RB Processed Date', varName: 'ProcessedDate', valueMask: 'date' },
  {
    label: 'Side',
    varName: 'Side',
    key: sideKey,
  },
  {
    label: 'Status',
    varName: 'ApprovalStatus',
    key: statusKey,
  },
  { label: 'Reviewer ID', varName: 'ApproverID' },
  { label: 'Bypass Type', varSet: 'Account', varName: 'BypassTypeDisplay' },
];
export const productInfoKey = [
  { label: 'Product Type', varName: 'ProductType', varSet: 'Display' },
  { label: 'Security ID', varName: 'SecID' },
  { label: 'Symbol', varName: 'Symbol' },
  { label: 'Name', varName: 'ProductName' },
  { label: 'Share Class', varName: 'CurrentShareClass', varSet: 'Display' },
  {
    label: 'Fund Family Name',
    varName: 'CurrentFundFamilyName',
    varSet: 'Display',
  },
  { label: 'UnitQuantity', varName: 'Quantity', varSet: 'Trade' },
  {
    label: 'Price',
    varName: 'SharePrice',
    valueMask: 'currency',
    varSet: 'Trade',
  },
  {
    label: 'Amount',
    varName: 'Amount',
    valueMask: 'currency',
    varSet: 'Trade',
  },
  {
    label: 'Sales Charge',
    varName: 'SalesCharge',
    valueMask: 'percent',
    varSet: 'Trade',
  },
  {
    label: 'Dealer Commission',
    varName: 'CommissionDollars',
    valueMask: 'currency',
    varSet: 'Trade',
  },
  {
    label: 'Solicited',
    varName: 'IsSolicited',
    key: yesNoKey,
    varSet: 'Trade',
  },
];
export const suitabilityKey = [
  {
    label: 'Entity Type',
    varSet: 'AccountHolder',
    varName: 'EntityType',
    key: entityKey,
  },
  { label: 'SSN/TIN', varSet: 'AccountHolder', varName: 'MaskedSSN' },
  { label: 'Age', varSet: 'AccountHolder', varName: 'Age' },
  { label: 'State of Residence', varSet: 'Display', varName: 'ResidentState' },
  {
    label: 'Liquid Net Worth',
    varSet: 'AccountHolder',
    varName: 'LiquidNetWorthDisplay',
  },
  {
    label: 'Net Worth',
    varSet: 'AccountHolder',
    varName: 'NetWorthDisplay',
  },
  {
    label: 'Income',
    varSet: 'AccountHolder',
    varName: 'IncomeDisplay',
  },
  {
    label: 'Tax Bracket',
    varSet: 'AccountHolder',
    varName: 'TaxBracketSource',
  },
  { label: 'Objective', varName: 'Objective' },
  {
    label: 'Risk Tolerance',
    varSet: 'Account',
    varName: 'RiskToleranceDisplay',
  },
  { label: 'Time Horizon', varSet: 'Account', varName: 'TimeHorizonDisplay' },
  {
    label: 'Liquidity Needs',
    varSet: 'AccountHolder',
    varName: 'LiquidityNeedsDisplay',
  },
];
export const accountKey = [
  {
    label: 'Account #',
    varSet: 'Account',
    varName: 'AccountNumDisplay',
    decryptTarget: 'EncryptedAccountID',
  },
  {
    label: 'Linked Direct Account',
    varSet: 'Trade',
    varName: 'AccountNumDisplay',
    rowLink: 'account-linking',
    conditional: (account: LinkedAccount) => account?.Status === 'L',
  },
  {
    label: 'Opened',
    varSet: 'Account',
    varName: 'OpenDate',
    valueMask: 'date',
  },
  {
    label: 'Balance',
    varSet: 'Account',
    varName: 'Balance',
    valueMask: 'currency',
  },
  { label: 'Account Type', varSet: 'Display', varName: 'AccountType' },
  { label: 'Registration', varSet: 'Display', varName: 'Registration' },
  { label: 'Tax Status', varSet: 'Display', varName: 'TaxStatus' },
  {
    label: 'Employee Owned',
    varSet: 'Account',
    varName: 'EmployeeOwned',
    key: yesNoKey,
  },
  { label: 'ROA Number', varSet: 'Trade', varName: 'ROANumber' },
  {
    label: 'LOI Number',
    varSet: 'Trade',
    varName: 'LOINumber',
  },
  {
    label: 'Compensation Type',
    varSet: 'Account',
    varName: 'CompType',
    key: {
      C: 'Commission',
      F: 'Fee',
    },
  },
  {
    label: 'Work/Application #',
    varSet: 'Account',
    varName: 'WorkID',
  },
  {
    label: 'Rep Company',
    varSet: 'Trade',
    varName: 'RepCompany',
  },
];
export const concurrentTradesKey = [
  { label: 'Trade Date', varName: 'TradeDate', valueMask: 'date' },
  { label: 'Side', varName: 'Side' },
  { label: 'Security ID', varName: 'SecID' },
  { label: 'Product Name', varName: 'Name' },
  { label: 'Share Class', varName: 'ShareClass' },
  { label: 'Quantity', varName: 'Quantity' },
  { label: 'Share Price', varName: 'SharePrice', valueMask: 'currency' },
  { label: 'Amount', varName: 'Amount', valueMask: 'currency' },
  { label: 'Sales Charge', varName: 'SalesCharge', valueMask: 'percent' },
];
export const breakpointAnalysisKey = [
  { label: 'Group ID', varSet: 'AccountGroup', varName: 'GroupID' },
  { label: 'Description', varSet: 'AccountGroup', varName: 'Description' },
  {
    label: '# of Linked Accounts',
    varSet: 'Display',
    varName: 'GroupAccountCount',
  },
  { label: 'Total Value', varSet: 'Display', varName: 'GroupSumBalance' },
];
export const tradeHistoryKey = [
  { label: 'Account #', varName: 'AccountNumDisplay' },
  { label: 'Trade Date', varName: 'TradeDate', valueMask: 'date' },
  {
    label: 'Side',
    varName: 'Side',
    key: sideKey,
  },
  {
    label: 'Product Type',
    varName: 'ProductType',
    key: productTypeKey,
  },
  { label: 'CUSIP', varName: 'SecID' },
  { label: 'Product Name', varName: 'ProductName' },
  { label: 'Amount', varName: 'Amount', valueMask: 'currency' },
  { label: 'Rep Code', varName: 'RepCode' },
  {
    label: 'Commission ($)',
    varName: 'CommissionDollars',
    valueMask: 'currency',
  },
];
export const tradeApprovalKey = [
  {
    label: 'Date Updated',
    varName: 'DateUpdated',
    valueMask: 'date',
  },
  {
    label: 'Status',
    varName: 'NewStatus',
    key: statusKey,
  },
  { label: 'Reviewer ID', varName: 'ReviewerID' },
];
export const tradePositionKey = [
  { label: 'Account #', varName: 'AccountNumDisplay' },
  { label: 'Security ID', varName: 'SecIDFX' },
  { label: 'Name', varName: 'Name' },
  { label: 'Product Type', varName: 'ProductType' },
  { label: 'Customer Objective', varName: 'Objective' },
  { label: 'Parent Policy #', varName: 'ContractID' },
  { label: 'Share Class', varName: 'ShareClass' },
  { label: 'Fund Family', varName: 'FundFamily' },
  { label: 'Acct. Registration', varName: 'Registration' },
  { label: 'Quantity', varName: 'QuantityFX' },
  { label: 'Unit Value', varName: 'UnitValueFX', valueMask: 'currency' },
  { label: 'Position Value', varName: 'PositionValue', valueMask: 'currency' },
];
export const annuityPositionKey = [
  { label: 'Carrier', varName: 'Carrier' },
  { label: 'Product Code', varName: 'ContractCode' },
  { label: 'Name', varName: 'Name' },
  { label: 'Type', varName: 'Type' },
  { label: 'Share Class', varName: 'ShareClass' },
  { label: 'Term', varName: 'Term' },
  { label: 'Purchase Date', varName: 'PurchaseDate' },
  { label: 'Current Value', varName: 'CurrentValue' },
];
export const associatedCaseKey = [
  { label: 'Case ID', varSet: 'RightBRIDGECase', varName: 'CaseID' },
  { label: 'Score Class', varSet: 'RightBRIDGECase', varName: 'ScoreClass' },
  {
    label: 'Date Generated',
    varSet: 'RightBRIDGECase',
    varName: 'DateGenerated',
    valueMask: 'date',
  },
  {
    label: 'Form CRS Delivery Date',
    varSet: 'ClientProfiling',
    varName: 'CRSDate',
  },
];
export const productModulesKey = {
  pp: 'Product Profiler',
  aw: 'Annuity Wizard',
  iw: 'Investment Wizard',
  li: 'Life Insurance Wizard',
};
export const chartOptions = {
  chart: {
    type: 'pie',
    backgroundColor: 'transparent',
    plotBackgroundColor: null,
    plotBorderWidth: null,
    plotShadow: false,
    options3d: {
      enabled: true,
      alpha: 45,
    },
  },
  title: {
    text: null,
  },
  plotOptions: {
    pie: {
      depth: 45,
      allowPointSelect: true,
      cursor: 'pointer',
      dataLabels: {
        enabled: false,
      },
      showInLegend: true,
    },
  },
  tooltip: {
    formatter: function () {
      return (
        this.key +
        ': <b>$' +
        Highcharts.numberFormat(this.y, 2, '.', ',') +
        '</b><br/>' +
        Highcharts.numberFormat(this.percentage, 2, '.', ',') +
        '%'
      );
    },
  },
  exporting: { enabled: false },
  credits: {
    enabled: false,
  },
  series: [],
};
